/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Admin Bank Wire Transfer Approval Center & Interactive Proof Viewer (Task 11.3.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Building2,
  CheckCircle2,
  XCircle,
  Clock,
  Search,
  RefreshCw,
  Download,
  ExternalLink,
  ZoomIn,
  ZoomOut,
  RotateCw,
  Maximize2,
  FileText,
  Check,
  X,
  AlertTriangle,
  Copy,
  Eye,
  Coins,
  DollarSign,
  Layers,
  ShieldCheck,
  AlertCircle,
} from 'lucide-react';
import { useI18n } from '../i18n';
import { adminClient } from '../lib/api-client';
import { DataTable, type ColumnDef, exportToCsv } from '../components/table/DataTable';
import type {
  AdminOrderListItem,
  AdminOrdersSummary,
} from '@sofia/shared-types';

const USD_TO_VND_RATE = 25400;

export const BankTransfersApprovalView: React.FC = () => {
  const { t, locale } = useI18n();
  const queryClient = useQueryClient();

  // Filters & State
  const [activeTab, setActiveTab] = useState<'pending' | 'paid' | 'failed' | 'all'>('pending');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const pageSize = 20;

  // Modals state
  const [viewingProofOrder, setViewingProofOrder] = useState<AdminOrderListItem | null>(null);
  const [approvingOrder, setApprovingOrder] = useState<AdminOrderListItem | null>(null);
  const [rejectingOrder, setRejectingOrder] = useState<AdminOrderListItem | null>(null);
  const [toastMessage, setToastMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const showToast = (type: 'success' | 'error', text: string) => {
    setToastMessage({ type, text });
    setTimeout(() => setToastMessage(null), 4000);
  };

  const handleCopy = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Currency Formatter
  const formatCurrency = (usdAmount: number) => {
    if (locale === 'vi') {
      const vnd = Math.round(usdAmount * USD_TO_VND_RATE);
      return (
        <div className="flex flex-col text-xs leading-tight">
          <span className="font-semibold text-foreground">
            {vnd.toLocaleString('vi-VN')} ₫
          </span>
          <span className="text-[10px] text-muted-fg font-mono">
            (${usdAmount.toFixed(2)})
          </span>
        </div>
      );
    }
    return (
      <div className="flex flex-col text-xs leading-tight">
        <span className="font-semibold text-foreground">
          ${usdAmount.toFixed(2)}
        </span>
        <span className="text-[10px] text-muted-fg font-mono">
          (~{(Math.round(usdAmount * USD_TO_VND_RATE)).toLocaleString()} ₫)
        </span>
      </div>
    );
  };

  // Format creation timestamp
  const formatDate = (isoString: string) => {
    try {
      const d = new Date(isoString);
      return (
        <div className="flex flex-col text-xs">
          <span className="text-foreground font-mono">
            {d.toLocaleDateString(locale === 'vi' ? 'vi-VN' : 'en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            })}
          </span>
          <span className="text-[10px] text-muted-fg font-mono">
            {d.toLocaleTimeString(locale === 'vi' ? 'vi-VN' : 'en-US', {
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
            })}
          </span>
        </div>
      );
    } catch {
      return isoString;
    }
  };

  // Query orders from D1 through Cloudflare API Worker
  const { data, isLoading, isFetching, refetch } = useQuery<{
    success: boolean;
    data: AdminOrderListItem[];
    total: number;
    summary: AdminOrdersSummary;
  }>({
    queryKey: ['admin-orders-bank-transfers', activeTab, searchQuery, page],
    queryFn: async () => {
      return adminClient.get('/api/v1/admin/orders/bank-transfers', {
        status: activeTab,
        gatewayCode: 'bank_transfer',
        search: searchQuery.trim() || undefined,
        page,
        limit: pageSize,
      });
    },
    staleTime: 10_000,
  });

  const orders = useMemo(() => data?.data || [], [data?.data]);
  const total = data?.total || 0;
  const summary: AdminOrdersSummary = data?.summary || {
    pendingCount: 0,
    approvedCount: 0,
    rejectedCount: 0,
    totalPendingAmountUsd: 0,
    totalApprovedAmountUsd: 0,
  };

  // Approve Mutation
  const approveMutation = useMutation({
    mutationFn: async ({ orderId, note }: { orderId: string; note?: string }) => {
      return adminClient.post(`/api/v1/admin/orders/${orderId}/approve`, { note });
    },
    onSuccess: (_, variables) => {
      showToast('success', t('admin.orders.successApprove'));
      queryClient.invalidateQueries({ queryKey: ['admin-orders-bank-transfers'] });
      setApprovingOrder(null);
      if (viewingProofOrder?.id === variables.orderId) {
        setViewingProofOrder(null);
      }
    },
    onError: (err: any) => {
      showToast('error', err.message || t('admin.orders.errorAction'));
    },
  });

  // Reject Mutation
  const rejectMutation = useMutation({
    mutationFn: async ({ orderId, reason }: { orderId: string; reason: string }) => {
      return adminClient.post(`/api/v1/admin/orders/${orderId}/reject`, { reason });
    },
    onSuccess: (_, variables) => {
      showToast('success', t('admin.orders.successReject'));
      queryClient.invalidateQueries({ queryKey: ['admin-orders-bank-transfers'] });
      setRejectingOrder(null);
      if (viewingProofOrder?.id === variables.orderId) {
        setViewingProofOrder(null);
      }
    },
    onError: (err: any) => {
      showToast('error', err.message || t('admin.orders.errorAction'));
    },
  });

  // Table Columns
  const columns: ColumnDef<AdminOrderListItem>[] = useMemo(() => [
    {
      id: 'orderId',
      header: t('admin.orders.colOrderId'),
      accessorKey: 'id',
      cell: ({ row }) => (
        <div className="flex items-center gap-1.5">
          <span className="font-mono text-xs font-semibold text-foreground">
            #{row.id.replace('ord_', '')}
          </span>
          <button
            type="button"
            onClick={() => handleCopy(row.id, row.id)}
            className="p-1 rounded text-muted-fg hover:text-foreground hover:bg-surface-elevated transition-colors"
            title={copiedId === row.id ? 'Copied' : 'Copy Full Order ID'}
          >
            {copiedId === row.id ? (
              <Check className="w-3 h-3 text-emerald-500" />
            ) : (
              <Copy className="w-3 h-3" />
            )}
          </button>
        </div>
      ),
    },
    {
      id: 'customer',
      header: t('admin.orders.colCustomer'),
      accessorFn: (row) => `${row.userName} ${row.userEmail} ${row.workspaceName}`,
      cell: ({ row }) => (
        <div className="flex flex-col space-y-0.5 max-w-[220px]">
          <span className="font-medium text-xs text-foreground truncate" title={row.userName}>
            {row.userName}
          </span>
          <span className="text-[11px] text-muted-fg truncate" title={row.userEmail}>
            {row.userEmail}
          </span>
          <div className="flex items-center gap-1 mt-0.5">
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-surface-elevated border border-surface-border text-foreground">
              <Layers className="w-2.5 h-2.5 text-primary" />
              <span className="truncate max-w-[120px]">{row.workspaceName || row.workspaceId}</span>
            </span>
          </div>
        </div>
      ),
    },
    {
      id: 'plan',
      header: t('admin.orders.colPlan'),
      accessorKey: 'planName',
      cell: ({ row }) => (
        <div className="flex flex-col space-y-1">
          <span className="font-medium text-xs text-foreground">
            {row.planName || row.planId}
          </span>
          <div className="flex items-center gap-1.5 flex-wrap">
            <span
              className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${
                row.planType === 'subscription'
                  ? 'bg-primary/10 text-primary border border-primary/20'
                  : 'bg-amber-500/10 text-amber-500 border border-amber-500/20'
              }`}
            >
              {row.planType === 'subscription'
                ? t('admin.plans.filterSubscription')
                : t('admin.plans.filterTokenPack')}
            </span>
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
              <Coins className="w-2.5 h-2.5" />
              +{(row.creditsIncluded || 0).toLocaleString()}
            </span>
          </div>
        </div>
      ),
    },
    {
      id: 'amount',
      header: t('admin.orders.colAmount'),
      accessorKey: 'amountPaid',
      cell: ({ row }) => formatCurrency(row.amountPaid),
    },
    {
      id: 'wireRef',
      header: t('admin.orders.colWireRef'),
      accessorKey: 'gatewayTransactionId',
      cell: ({ row }) => {
        const ref = row.gatewayTransactionId || '-';
        return (
          <div className="flex items-center gap-1.5">
            <span className="font-mono text-xs text-foreground bg-surface-elevated px-2 py-1 rounded border border-surface-border">
              {ref}
            </span>
            {row.gatewayTransactionId && (
              <button
                type="button"
                onClick={() => handleCopy(row.gatewayTransactionId!, `wire_${row.id}`)}
                className="p-1 rounded text-muted-fg hover:text-foreground hover:bg-surface-elevated transition-colors"
                title="Copy Wire Code"
              >
                {copiedId === `wire_${row.id}` ? (
                  <Check className="w-3 h-3 text-emerald-500" />
                ) : (
                  <Copy className="w-3 h-3" />
                )}
              </button>
            )}
          </div>
        );
      },
    },
    {
      id: 'proof',
      header: t('admin.orders.colProof'),
      cell: ({ row }) => {
        if (!row.proofUrl) {
          return (
            <span className="text-[11px] text-muted-fg italic flex items-center gap-1">
              <AlertCircle className="w-3 h-3 text-amber-500/70" />
              {t('admin.orders.noProof')}
            </span>
          );
        }

        return (
          <button
            type="button"
            onClick={() => setViewingProofOrder(row)}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-surface-elevated hover:bg-primary/10 border border-surface-border hover:border-primary/30 text-foreground hover:text-primary transition-all group"
          >
            <div className="w-5 h-5 rounded overflow-hidden bg-black/20 shrink-0 border border-surface-border">
              <img
                src={row.proofUrl}
                alt="Proof thumbnail"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-200"
                loading="lazy"
              />
            </div>
            <span>{t('admin.orders.viewProof')}</span>
            <Eye className="w-3 h-3 text-muted-fg group-hover:text-primary" />
          </button>
        );
      },
    },
    {
      id: 'createdAt',
      header: t('admin.orders.colDate'),
      accessorKey: 'createdAt',
      cell: ({ row }) => formatDate(row.createdAt),
    },
    {
      id: 'status',
      header: t('admin.orders.colStatus'),
      accessorKey: 'status',
      cell: ({ row }) => {
        const statusConfig = {
          pending: {
            bg: 'bg-amber-500/10 border-amber-500/20 text-amber-500',
            dot: 'bg-amber-500',
            label: t('admin.orders.pendingReview'),
          },
          paid: {
            bg: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-500',
            dot: 'bg-emerald-500',
            label: t('admin.orders.approved'),
          },
          failed: {
            bg: 'bg-destructive/10 border-destructive/20 text-destructive',
            dot: 'bg-destructive',
            label: t('admin.orders.rejected'),
          },
          refunded: {
            bg: 'bg-muted border-surface-border text-foreground-muted',
            dot: 'bg-foreground-muted',
            label: 'Refunded',
          },
        }[row.status] || {
          bg: 'bg-surface-elevated border-surface-border text-muted-fg',
          dot: 'bg-muted-fg',
          label: row.status,
        };

        return (
          <div className="flex flex-col gap-1">
            <span
              className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold border ${statusConfig.bg}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full animate-pulse ${statusConfig.dot}`} />
              {statusConfig.label}
            </span>
            {row.status === 'failed' && row.rejectionReason && (
              <span
                className="text-[10px] text-destructive italic truncate max-w-[140px]"
                title={row.rejectionReason}
              >
                {row.rejectionReason}
              </span>
            )}
          </div>
        );
      },
    },
    {
      id: 'actions',
      header: t('admin.orders.colActions'),
      cell: ({ row }) => {
        if (row.status === 'pending') {
          return (
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => setApprovingOrder(row)}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-emerald-500 text-white hover:bg-emerald-600 transition-colors shadow-sm"
                title={t('admin.orders.approveOrder')}
              >
                <Check className="w-3.5 h-3.5" />
                <span>{t('admin.orders.approveOrder')}</span>
              </button>
              <button
                type="button"
                onClick={() => setRejectingOrder(row)}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium text-destructive hover:bg-destructive/10 border border-destructive/20 transition-colors"
                title={t('admin.orders.rejectOrder')}
              >
                <X className="w-3.5 h-3.5" />
                <span>{t('admin.orders.rejectOrder')}</span>
              </button>
            </div>
          );
        }

        if (row.status === 'paid') {
          return (
            <span className="inline-flex items-center gap-1 text-xs text-emerald-500 font-medium">
              <ShieldCheck className="w-4 h-4" />
              {t('admin.orders.approved')}
            </span>
          );
        }

        if (row.status === 'failed') {
          return (
            <button
              type="button"
              onClick={() => {
                if (row.rejectionReason) {
                  alert(`Rejection Reason:\n\n${row.rejectionReason}`);
                }
              }}
              className="inline-flex items-center gap-1 text-xs text-muted-fg hover:text-foreground transition-colors"
            >
              <AlertCircle className="w-3.5 h-3.5 text-destructive" />
              <span>Reason</span>
            </button>
          );
        }

        return <span className="text-xs text-muted-fg">-</span>;
      },
    },
  ], [locale, t, copiedId]);

  // CSV export handler
  const handleExportCsv = () => {
    exportToCsv(
      columns,
      orders,
      `bank-transfers-${activeTab}-${new Date().toISOString().slice(0, 10)}.csv`
    );
  };

  return (
    <div className="space-y-6">
      {/* Toast Notification */}
      {toastMessage && (
        <div
          className={`fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-xl text-xs font-medium animate-in fade-in slide-in-from-bottom-3 duration-200 ${
            toastMessage.type === 'success'
              ? 'bg-emerald-600 text-white'
              : 'bg-destructive text-white'
          }`}
        >
          {toastMessage.type === 'success' ? (
            <CheckCircle2 className="w-4 h-4" />
          ) : (
            <AlertTriangle className="w-4 h-4" />
          )}
          <span>{toastMessage.text}</span>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-surface-border">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
              <Building2 className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-xl font-heading font-bold text-foreground">
                {t('admin.orders.title')}
              </h1>
              <p className="text-xs text-muted-fg mt-0.5">
                {t('admin.orders.subtitle')}
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium bg-surface-elevated hover:bg-surface border border-surface-border text-foreground transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isFetching ? 'animate-spin text-primary' : ''}`} />
            <span>{t('admin.orders.refresh')}</span>
          </button>
          <button
            type="button"
            onClick={handleExportCsv}
            disabled={orders.length === 0}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium bg-surface-elevated hover:bg-surface border border-surface-border text-foreground transition-colors disabled:opacity-50"
          >
            <Download className="w-3.5 h-3.5" />
            <span>{t('admin.orders.exportCsv')}</span>
          </button>
        </div>
      </div>

      {/* 4 Telemetry KPI Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* KPI 1: Pending Count */}
        <div className="p-4 rounded-2xl bg-surface border border-surface-border shadow-sm flex items-center justify-between relative overflow-hidden">
          <div className="space-y-1">
            <span className="text-[11px] font-semibold text-muted-fg uppercase tracking-wider">
              {t('admin.orders.kpiPendingCount')}
            </span>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-heading font-bold text-amber-500">
                {summary.pendingCount}
              </span>
              <span className="text-[10px] text-muted-fg">orders</span>
            </div>
            <p className="text-[11px] text-muted-fg">{t('admin.orders.kpiPendingSub')}</p>
          </div>
          <div className="w-11 h-11 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-500">
            <Clock className="w-5 h-5" />
          </div>
        </div>

        {/* KPI 2: Approved Count */}
        <div className="p-4 rounded-2xl bg-surface border border-surface-border shadow-sm flex items-center justify-between relative overflow-hidden">
          <div className="space-y-1">
            <span className="text-[11px] font-semibold text-muted-fg uppercase tracking-wider">
              {t('admin.orders.kpiApprovedCount')}
            </span>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-heading font-bold text-emerald-500">
                {summary.approvedCount}
              </span>
              <span className="text-[10px] text-muted-fg">orders</span>
            </div>
            <p className="text-[11px] text-muted-fg">{t('admin.orders.kpiApprovedSub')}</p>
          </div>
          <div className="w-11 h-11 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-500">
            <CheckCircle2 className="w-5 h-5" />
          </div>
        </div>

        {/* KPI 3: Rejected Count */}
        <div className="p-4 rounded-2xl bg-surface border border-surface-border shadow-sm flex items-center justify-between relative overflow-hidden">
          <div className="space-y-1">
            <span className="text-[11px] font-semibold text-muted-fg uppercase tracking-wider">
              {t('admin.orders.kpiRejectedCount')}
            </span>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-heading font-bold text-destructive">
                {summary.rejectedCount}
              </span>
              <span className="text-[10px] text-muted-fg">orders</span>
            </div>
            <p className="text-[11px] text-muted-fg">{t('admin.orders.kpiRejectedSub')}</p>
          </div>
          <div className="w-11 h-11 rounded-xl bg-destructive/10 border border-destructive/20 flex items-center justify-center text-destructive">
            <XCircle className="w-5 h-5" />
          </div>
        </div>

        {/* KPI 4: Pending Volume */}
        <div className="p-4 rounded-2xl bg-surface border border-surface-border shadow-sm flex items-center justify-between relative overflow-hidden">
          <div className="space-y-1">
            <span className="text-[11px] font-semibold text-muted-fg uppercase tracking-wider">
              {t('admin.orders.kpiPendingVolume')}
            </span>
            <div className="flex items-baseline gap-2">
              <span className="text-xl font-heading font-bold text-foreground">
                ${summary.totalPendingAmountUsd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
              {locale === 'vi' && (
                <span className="text-[11px] text-muted-fg font-mono">
                  (~{Math.round(summary.totalPendingAmountUsd * USD_TO_VND_RATE).toLocaleString()} ₫)
                </span>
              )}
            </div>
            <p className="text-[11px] text-muted-fg">{t('admin.orders.kpiPendingVolumeSub')}</p>
          </div>
          <div className="w-11 h-11 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
            <DollarSign className="w-5 h-5" />
          </div>
        </div>
      </div>

      {/* Tabs & Search Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-2 bg-surface rounded-2xl border border-surface-border">
        {/* Status Tabs */}
        <div className="flex items-center gap-1 overflow-x-auto">
          <button
            type="button"
            onClick={() => {
              setActiveTab('pending');
              setPage(1);
            }}
            className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all shrink-0 ${
              activeTab === 'pending'
                ? 'bg-amber-500 text-white shadow-sm'
                : 'text-muted-fg hover:text-foreground hover:bg-surface-elevated'
            }`}
          >
            <Clock className="w-3.5 h-3.5" />
            <span>{t('admin.orders.tabPending')}</span>
            {summary.pendingCount > 0 && (
              <span
                className={`px-1.5 py-0.2 rounded-full text-[10px] font-bold ${
                  activeTab === 'pending'
                    ? 'bg-white/20 text-white'
                    : 'bg-amber-500/20 text-amber-500'
                }`}
              >
                {summary.pendingCount}
              </span>
            )}
          </button>

          <button
            type="button"
            onClick={() => {
              setActiveTab('paid');
              setPage(1);
            }}
            className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all shrink-0 ${
              activeTab === 'paid'
                ? 'bg-emerald-600 text-white shadow-sm'
                : 'text-muted-fg hover:text-foreground hover:bg-surface-elevated'
            }`}
          >
            <CheckCircle2 className="w-3.5 h-3.5" />
            <span>{t('admin.orders.tabApproved')}</span>
          </button>

          <button
            type="button"
            onClick={() => {
              setActiveTab('failed');
              setPage(1);
            }}
            className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all shrink-0 ${
              activeTab === 'failed'
                ? 'bg-destructive text-white shadow-sm'
                : 'text-muted-fg hover:text-foreground hover:bg-surface-elevated'
            }`}
          >
            <XCircle className="w-3.5 h-3.5" />
            <span>{t('admin.orders.tabRejected')}</span>
          </button>

          <button
            type="button"
            onClick={() => {
              setActiveTab('all');
              setPage(1);
            }}
            className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all shrink-0 ${
              activeTab === 'all'
                ? 'bg-primary text-primary-fg shadow-sm'
                : 'text-muted-fg hover:text-foreground hover:bg-surface-elevated'
            }`}
          >
            <Layers className="w-3.5 h-3.5" />
            <span>{t('admin.orders.tabAll')}</span>
          </button>
        </div>

        {/* Search input */}
        <div className="relative w-full sm:w-80">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-fg" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(1);
            }}
            placeholder={t('admin.orders.searchPlaceholder')}
            className="w-full pl-9 pr-8 py-1.5 text-xs rounded-xl bg-surface-elevated border border-surface-border text-foreground placeholder:text-muted-fg focus:outline-none focus:border-primary transition-colors"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => {
                setSearchQuery('');
                setPage(1);
              }}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded text-muted-fg hover:text-foreground"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>

      {/* TanStack Data Table */}
      <DataTable
        columns={columns}
        data={orders}
        isLoading={isLoading}
        totalCount={total}
        pageIndex={page - 1}
        pageSize={pageSize}
        onPageChange={(p) => setPage(p + 1)}
        isServerSide={true}
        enableSearch={false}
      />

      {/* Modal 1: Interactive High-Resolution R2 Proof Viewer */}
      {viewingProofOrder && (
        <ProofReceiptModal
          order={viewingProofOrder}
          onClose={() => setViewingProofOrder(null)}
          onApprove={() => setApprovingOrder(viewingProofOrder)}
          onReject={() => setRejectingOrder(viewingProofOrder)}
          formatCurrency={formatCurrency}
        />
      )}

      {/* Modal 2: Approve Order Modal */}
      {approvingOrder && (
        <ApproveOrderModal
          order={approvingOrder}
          isPending={approveMutation.isPending}
          onClose={() => setApprovingOrder(null)}
          onConfirm={(note) =>
            approveMutation.mutate({ orderId: approvingOrder.id, note })
          }
          formatCurrency={formatCurrency}
        />
      )}

      {/* Modal 3: Reject Order Modal */}
      {rejectingOrder && (
        <RejectOrderModal
          order={rejectingOrder}
          isPending={rejectMutation.isPending}
          onClose={() => setRejectingOrder(null)}
          onConfirm={(reason) =>
            rejectMutation.mutate({ orderId: rejectingOrder.id, reason })
          }
          formatCurrency={formatCurrency}
        />
      )}
    </div>
  );
};

/**
 * ============================================================================
 * MODAL 1: Interactive High-Resolution R2 Proof Receipt Viewer
 * ============================================================================
 */
interface ProofReceiptModalProps {
  order: AdminOrderListItem;
  onClose: () => void;
  onApprove: () => void;
  onReject: () => void;
  formatCurrency: (amount: number) => React.ReactNode;
}

const ProofReceiptModal: React.FC<ProofReceiptModalProps> = ({
  order,
  onClose,
  onApprove,
  onReject,
  formatCurrency,
}) => {
  const { t } = useI18n();
  const [zoom, setZoom] = useState<number>(1);
  const [rotation, setRotation] = useState<number>(0);
  const [imgError, setImgError] = useState(false);

  const handleZoomIn = () => setZoom((prev) => Math.min(3, Math.round((prev + 0.25) * 100) / 100));
  const handleZoomOut = () => setZoom((prev) => Math.max(0.5, Math.round((prev - 0.25) * 100) / 100));
  const handleReset = () => {
    setZoom(1);
    setRotation(0);
  };
  const handleRotate = () => setRotation((prev) => (prev + 90) % 360);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-md p-4 sm:p-6 animate-in fade-in duration-150">
      <div className="w-full max-w-5xl h-[85vh] rounded-3xl bg-surface border border-surface-border shadow-2xl flex flex-col overflow-hidden">
        {/* Viewer Top Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-border bg-surface-elevated shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
              <FileText className="w-4 h-4" />
            </div>
            <div>
              <h3 className="text-sm font-heading font-bold text-foreground">
                {t('admin.orders.modalProofTitle')}
              </h3>
              <p className="text-[11px] text-muted-fg font-mono">
                Order #{order.id} &bull; Wire Ref: {order.gatewayTransactionId || 'N/A'}
              </p>
            </div>
          </div>

          {/* Interactive Zoom & Rotate Toolbar */}
          <div className="flex items-center gap-1 bg-surface px-2 py-1 rounded-xl border border-surface-border">
            <button
              type="button"
              onClick={handleZoomOut}
              disabled={zoom <= 0.5}
              className="p-1.5 rounded-lg text-muted-fg hover:text-foreground hover:bg-surface-elevated transition-colors disabled:opacity-40"
              title={t('admin.orders.zoomOut')}
            >
              <ZoomOut className="w-4 h-4" />
            </button>
            <span className="text-[11px] font-mono font-semibold px-1 text-foreground min-w-[42px] text-center">
              {Math.round(zoom * 100)}%
            </span>
            <button
              type="button"
              onClick={handleZoomIn}
              disabled={zoom >= 3}
              className="p-1.5 rounded-lg text-muted-fg hover:text-foreground hover:bg-surface-elevated transition-colors disabled:opacity-40"
              title={t('admin.orders.zoomIn')}
            >
              <ZoomIn className="w-4 h-4" />
            </button>
            <div className="w-px h-4 bg-surface-border mx-1" />
            <button
              type="button"
              onClick={handleRotate}
              className="p-1.5 rounded-lg text-muted-fg hover:text-foreground hover:bg-surface-elevated transition-colors"
              title={t('admin.orders.rotate')}
            >
              <RotateCw className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={handleReset}
              className="p-1.5 rounded-lg text-muted-fg hover:text-foreground hover:bg-surface-elevated transition-colors text-xs"
              title={t('admin.orders.fitScreen')}
            >
              <Maximize2 className="w-4 h-4" />
            </button>
            {order.proofUrl && (
              <>
                <div className="w-px h-4 bg-surface-border mx-1" />
                <a
                  href={order.proofUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-1.5 rounded-lg text-muted-fg hover:text-foreground hover:bg-surface-elevated transition-colors"
                  title={t('admin.orders.openNewTab')}
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
              </>
            )}
          </div>

          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-xl text-muted-fg hover:text-foreground hover:bg-surface transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Viewer Main Body (Canvas + Metadata Sidebar) */}
        <div className="flex-1 flex flex-col md:flex-row overflow-hidden bg-black/40">
          {/* Proof Canvas */}
          <div className="flex-1 overflow-auto flex items-center justify-center p-6 relative select-none">
            {imgError ? (
              <div className="text-center p-8 text-muted-fg space-y-2">
                <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto" />
                <p className="text-sm font-medium">Failed to load high-resolution proof receipt</p>
                <p className="text-xs text-muted-fg/80 font-mono">{order.proofUrl}</p>
              </div>
            ) : order.proofUrl ? (
              <div
                className="transition-transform duration-150 ease-out flex items-center justify-center"
                style={{
                  transform: `scale(${zoom}) rotate(${rotation}deg)`,
                  transformOrigin: 'center center',
                }}
              >
                <img
                  src={order.proofUrl}
                  alt="Bank transfer wire proof receipt"
                  onError={() => setImgError(true)}
                  className="max-h-[65vh] max-w-full rounded-lg shadow-2xl object-contain border border-white/10"
                />
              </div>
            ) : (
              <div className="text-center p-8 text-muted-fg space-y-2">
                <AlertCircle className="w-10 h-10 text-muted-fg mx-auto" />
                <p className="text-sm font-medium">{t('admin.orders.noProof')}</p>
              </div>
            )}
          </div>

          {/* Right Metadata & Verification Sidebar */}
          <div className="w-full md:w-80 border-t md:border-t-0 md:border-l border-surface-border bg-surface flex flex-col shrink-0">
            <div className="p-5 border-b border-surface-border bg-surface-elevated/50">
              <span className="text-[10px] font-bold uppercase tracking-wider text-muted-fg">
                {t('admin.orders.orderDetails')}
              </span>
              <h4 className="text-base font-heading font-bold text-foreground mt-1">
                {order.planName}
              </h4>
              <div className="mt-2">{formatCurrency(order.amountPaid)}</div>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-4 text-xs">
              <div>
                <span className="text-[11px] font-semibold text-muted-fg block mb-0.5">
                  {t('admin.orders.user')}
                </span>
                <span className="font-medium text-foreground block">{order.userName}</span>
                <span className="text-muted-fg text-[11px] block">{order.userEmail}</span>
              </div>

              <div>
                <span className="text-[11px] font-semibold text-muted-fg block mb-0.5">
                  {t('admin.orders.workspace')}
                </span>
                <span className="font-medium text-foreground block">
                  {order.workspaceName || order.workspaceId}
                </span>
              </div>

              <div>
                <span className="text-[11px] font-semibold text-muted-fg block mb-0.5">
                  {t('admin.orders.colWireRef')}
                </span>
                <span className="font-mono text-foreground bg-surface-elevated px-2 py-1 rounded border border-surface-border inline-block">
                  {order.gatewayTransactionId || 'None'}
                </span>
              </div>

              <div>
                <span className="text-[11px] font-semibold text-muted-fg block mb-0.5">
                  {t('admin.orders.creditsGranted')}
                </span>
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                  <Coins className="w-3 h-3" />
                  +{(order.creditsIncluded || 0).toLocaleString()} Credits
                </span>
              </div>

              <div>
                <span className="text-[11px] font-semibold text-muted-fg block mb-0.5">
                  {t('admin.orders.colDate')}
                </span>
                <span className="text-muted-fg text-[11px] font-mono">
                  {new Date(order.createdAt).toLocaleString()}
                </span>
              </div>
            </div>

            {/* Quick Actions Footer inside Modal */}
            {order.status === 'pending' && (
              <div className="p-4 border-t border-surface-border bg-surface-elevated space-y-2">
                <button
                  type="button"
                  onClick={onApprove}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-semibold bg-emerald-500 text-white hover:bg-emerald-600 transition-colors shadow-sm"
                >
                  <Check className="w-4 h-4" />
                  <span>{t('admin.orders.approveOrder')}</span>
                </button>
                <button
                  type="button"
                  onClick={onReject}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold text-destructive hover:bg-destructive/10 border border-destructive/20 transition-colors"
                >
                  <X className="w-4 h-4" />
                  <span>{t('admin.orders.rejectOrder')}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * ============================================================================
 * MODAL 2: Approve Bank Wire Order Modal
 * ============================================================================
 */
interface ApproveOrderModalProps {
  order: AdminOrderListItem;
  isPending: boolean;
  onClose: () => void;
  onConfirm: (note?: string) => void;
  formatCurrency: (amount: number) => React.ReactNode;
}

const ApproveOrderModal: React.FC<ApproveOrderModalProps> = ({
  order,
  isPending,
  onClose,
  onConfirm,
  formatCurrency,
}) => {
  const { t } = useI18n();
  const [note, setNote] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onConfirm(note.trim() || undefined);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-150">
      <div className="w-full max-w-lg rounded-3xl bg-surface border border-surface-border shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-border bg-surface-elevated">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-500">
              <CheckCircle2 className="w-4 h-4" />
            </div>
            <h3 className="text-sm font-heading font-bold text-foreground">
              {t('admin.orders.modalApproveTitle')}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-muted-fg hover:text-foreground hover:bg-surface transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <p className="text-xs text-muted-fg leading-relaxed">
            {t('admin.orders.modalApproveDesc', {
              credits: (order.creditsIncluded || 0).toLocaleString(),
            })}
          </p>

          {/* Order Summary Box */}
          <div className="p-3.5 rounded-2xl bg-surface-elevated border border-surface-border space-y-2 text-xs">
            <div className="flex items-center justify-between">
              <span className="text-muted-fg">Order ID:</span>
              <span className="font-mono font-semibold text-foreground">#{order.id}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-fg">Customer:</span>
              <span className="font-medium text-foreground">{order.userName} ({order.userEmail})</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-fg">Plan / Pack:</span>
              <span className="font-medium text-foreground">{order.planName}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-fg">Amount:</span>
              <div>{formatCurrency(order.amountPaid)}</div>
            </div>
            <div className="flex items-center justify-between pt-1 border-t border-surface-border">
              <span className="text-muted-fg font-medium">Wallet Deposit:</span>
              <span className="font-bold text-emerald-500">
                +{(order.creditsIncluded || 0).toLocaleString()} Credits
              </span>
            </div>
          </div>

          {/* Internal Administrative Note */}
          <div>
            <label className="block text-[11px] font-semibold text-muted-fg mb-1">
              {t('admin.orders.adminNoteLabel')}
            </label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t('admin.orders.adminNotePlaceholder')}
              className="w-full px-3 py-2 text-xs rounded-xl bg-surface-elevated border border-surface-border text-foreground placeholder:text-muted-fg focus:outline-none focus:border-primary transition-colors"
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isPending}
              className="px-4 py-2 rounded-xl text-xs font-medium text-muted-fg hover:text-foreground hover:bg-surface-elevated transition-colors"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold bg-emerald-500 text-white hover:bg-emerald-600 transition-colors shadow-sm disabled:opacity-50"
            >
              {isPending ? (
                <>
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                  <span>{t('admin.orders.approving')}</span>
                </>
              ) : (
                <>
                  <Check className="w-3.5 h-3.5" />
                  <span>{t('admin.orders.confirmApproveBtn')}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/**
 * ============================================================================
 * MODAL 3: Reject Bank Wire Order Modal
 * ============================================================================
 */
interface RejectOrderModalProps {
  order: AdminOrderListItem;
  isPending: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  formatCurrency: (amount: number) => React.ReactNode;
}

const RejectOrderModal: React.FC<RejectOrderModalProps> = ({
  order,
  isPending,
  onClose,
  onConfirm,
  formatCurrency,
}) => {
  const { t } = useI18n();
  const [reason, setReason] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason.trim() || reason.trim().length < 3) {
      setValidationError(t('admin.orders.rejectReasonRequired'));
      return;
    }
    setValidationError(null);
    onConfirm(reason.trim());
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-150">
      <div className="w-full max-w-lg rounded-3xl bg-surface border border-surface-border shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-border bg-surface-elevated">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-destructive/10 border border-destructive/20 flex items-center justify-center text-destructive">
              <XCircle className="w-4 h-4" />
            </div>
            <h3 className="text-sm font-heading font-bold text-foreground">
              {t('admin.orders.modalRejectTitle')}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-muted-fg hover:text-foreground hover:bg-surface transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <p className="text-xs text-muted-fg leading-relaxed">
            {t('admin.orders.modalRejectDesc')}
          </p>

          {/* Order Details Brief */}
          <div className="p-3.5 rounded-2xl bg-surface-elevated border border-surface-border space-y-1.5 text-xs">
            <div className="flex items-center justify-between">
              <span className="text-muted-fg">Order ID:</span>
              <span className="font-mono font-semibold text-foreground">#{order.id}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-fg">Customer:</span>
              <span className="font-medium text-foreground">{order.userName} ({order.userEmail})</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-fg">Amount:</span>
              <div>{formatCurrency(order.amountPaid)}</div>
            </div>
          </div>

          {/* Mandatory Rejection Reason */}
          <div>
            <label className="block text-[11px] font-semibold text-foreground mb-1">
              {t('admin.orders.rejectReasonLabel')} <span className="text-destructive">*</span>
            </label>
            <textarea
              required
              rows={3}
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                if (validationError) setValidationError(null);
              }}
              placeholder={t('admin.orders.rejectReasonPlaceholder')}
              className="w-full px-3 py-2 text-xs rounded-xl bg-surface-elevated border border-surface-border text-foreground placeholder:text-muted-fg focus:outline-none focus:border-destructive transition-colors resize-none"
            />
            {validationError && (
              <p className="text-[11px] text-destructive mt-1 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {validationError}
              </p>
            )}
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isPending}
              className="px-4 py-2 rounded-xl text-xs font-medium text-muted-fg hover:text-foreground hover:bg-surface-elevated transition-colors"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold bg-destructive text-white hover:bg-destructive/90 transition-colors shadow-sm disabled:opacity-50"
            >
              {isPending ? (
                <>
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                  <span>{t('admin.orders.rejecting')}</span>
                </>
              ) : (
                <>
                  <X className="w-3.5 h-3.5" />
                  <span>{t('admin.orders.confirmRejectBtn')}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
